/**
 * BatchScopeMenu — the Order Queue's `Create Batch (N) ▾` control (BLU-777).
 *
 * Concept A (Brett, 2026-08-12): ONE control on the right, not a row of buttons
 * and not a card board. The org's configured scopes are CHECKBOXES, so ticking
 * several yields a deduped union — an order in two scopes counts once.
 *
 * ⚠ EVERY NUMBER HERE COMES FROM THE SERVER. The page holds one 30-row page, so
 * counting matches in the browser would show "Express (48)" and then create a
 * batch of ~4. `resolveBatchScopes` resolves the rule set against the whole
 * eligible queue and returns both the per-scope counts and the union total.
 * Nothing in this component may derive a count from the loaded rows.
 *
 * Design language: this page nests under ProductionLayout, but
 * PRODUCTION_DESIGN.md §5.4 names the Order Queue a DESK-SIDE surface and the
 * page already uses inventory grammar throughout (h-8 px-3 text-xs controls,
 * 200ms framer-motion). Mirrors PoActionBar's labelled-trigger-with-chevron
 * dropdown — no scan-station conventions (no audio, no 48px targets).
 */

import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import { AlertCircle, ChevronDown, Info, Layers, Loader2, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  DropdownMenu,
  DropdownMenuCheckboxItem,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import { EXPRESS_BATCH_ACTION_ID } from '@threadheads/shared';
import { cn } from '@/lib/utils';

export interface BatchScopeOption {
  isTagScope?: boolean;
  id: string;
  name: string;
  /** Live count over the whole eligible queue — server-resolved. */
  count: number;
}

interface BatchScopeMenuProps {
  scopes: BatchScopeOption[];
  /** Ticked scope ids. OWN state — never `useOrderSelection`, which prunes to the page. */
  checkedScopeIds: Set<string>;
  onToggleScope: (scopeId: string) => void;
  onClearScopes: () => void;
  /** Deduped union size across the ticked scopes (+ hand-ticked rows), BEFORE the cap. */
  unionTotal: number;
  /** How many the batch would actually take (union trimmed to `cap`). */
  unionTaken: number;
  /** True when `unionTotal > unionTaken`. */
  trimmed: boolean;
  /** The batch-wide cap (200). Passed in so the copy can never drift from the server. */
  cap: number;
  /**
   * Hand-ticked rows that SURVIVED eligibility and are in the union — not the
   * number sent. An order the ready tab excludes (Portless-blocked, time-held,
   * OOS) is dropped by the resolver, and reporting the sent count here would be
   * the same kind of quiet over-count this whole control exists to avoid.
   */
  handPickedCount: number;
  /** Hand-ticked rows the resolver discarded as no longer batch-eligible. */
  droppedHandPicked: number;
  /** Sum of the ticked scopes' individual counts — only shown when it exceeds the union. */
  tickedScopeSum: number;
  /**
   * Union of the ticked SCOPES ONLY, server-computed. Overlap is
   * `tickedScopeSum - scopeUnionTotal`; inferring it from `unionTotal` instead
   * double-subtracts any row that was both hand-ticked and scope-matched.
   */
  scopeUnionTotal: number;
  isLoading: boolean;
  /**
   * The resolver failed. MUST be distinguished from "no scopes configured" —
   * both leave `scopes` empty, but telling an operator who has buttons that they
   * have none sends them to Settings to re-create what already exists.
   *
   * Required, not optional-with-default: a `= false` default would let a future
   * call site forget it and silently re-arm that exact bug with no type error.
   */
  isError: boolean;
  /** Re-run the resolver from the error state. */
  onRetry: () => void;
  disabled?: boolean;
  /** A create is in flight — drives the trigger spinner alongside `isLoading`. */
  isCreating?: boolean;
  onPreview: () => void;
  /**
   * BLU-787 — create straight from the resolved union, skipping the preview.
   *
   * Receives no ids: the resolved set lives on the page, and handing the menu a
   * list would re-open the PR #1659 class where a control builds a batch from
   * what it happens to be holding rather than what the server resolved.
   */
  onCreateNow: () => void;
  /** BLU-787 — the two injection gates, tickable inside the menu. Both default ON. */
  includeRedos: boolean;
  onIncludeRedosChange: (next: boolean) => void;
  includeRequeued: boolean;
  onIncludeRequeuedChange: (next: boolean) => void;
  /** Redo GARMENTS that will be attached, already capped. */
  redoCount: number;
  /** Redo garments PENDING before the cap, so the trim can be stated. */
  redoTotalPending: number;
  /** Re-queued ORDERS that will be injected. Undefined while resolving. */
  requeuedCount?: number;
  /**
   * BLU-787 disclosures — the resolver's reach does not always match what the
   * screen implies, and staying silent about the gap is how PR #1659-shaped
   * bugs happen again in a new spot. Each one is a fact about `resolveBatchScopes`
   * itself (see its handler docblock), not a UI guess:
   *  - `screenSortOrder`: the resolver always sorts oldest-first for the
   *    200-cap trim, regardless of how the screen is sorted.
   *  - `screenQueueStatus`: the resolver always reads the ready tab, so on the
   *    All view the list count and this menu's counts can legitimately differ.
   *  - `hasActiveSearch`: `search` is not representable in the resolver's
   *    input at all — it narrows the list on screen only.
   *  - `isClientFiltered`: the redo/re-queued injections read every accessible
   *    org (redos) or the caller's org only (re-queued) — neither follows the
   *    client filter, because `createBatch` never sees it either.
   */
  screenSortOrder: 'oldest' | 'newest';
  screenQueueStatus: 'ready' | 'all';
  hasActiveSearch: boolean;
  isClientFiltered: boolean;
}

export function BatchScopeMenu({
  scopes,
  checkedScopeIds,
  onToggleScope,
  onClearScopes,
  unionTotal,
  unionTaken,
  trimmed,
  cap,
  handPickedCount,
  droppedHandPicked,
  tickedScopeSum,
  scopeUnionTotal,
  isLoading,
  isError,
  onRetry,
  disabled = false,
  isCreating = false,
  onPreview,
  onCreateNow,
  includeRedos,
  onIncludeRedosChange,
  includeRequeued,
  onIncludeRequeuedChange,
  redoCount,
  redoTotalPending,
  requeuedCount,
  screenSortOrder,
  screenQueueStatus,
  hasActiveSearch,
  isClientFiltered,
}: BatchScopeMenuProps) {
  const { t } = useTranslation('production');
  const { t: settingsT } = useTranslation('settings');

  // Three distinct empty-looking states, and conflating them is the bug this
  // guards: still resolving (spinner), failed (retryable error), and genuinely
  // unconfigured (the Settings CTA). Only the last one may say "not set up yet".
  const scopeListState =
    scopes.length > 0 ? 'ready' : isError ? 'error' : isLoading ? 'pending' : 'unconfigured';
  const tickedCount = checkedScopeIds.size;
  /**
   * ⚠ An unknown count renders as a dash, never as 0.
   *
   * `BatchInclusionToggle` states the same rule and this file broke it: while the
   * resolver is pending or has failed there is no `scopeResolution`, so the
   * counts arrived here coerced to 0 and the menu rendered two ticked, default-ON
   * controls reading "0 garments" and "0 orders" — directly beneath "Couldn't
   * load your batch buttons". A fabricated zero next to an admission that nothing
   * loaded is worse than no number: it reads as "there is no redo work", which is
   * exactly the belief that lets redos stop being made.
   */
  const countsKnown = !isLoading && !isError;
  const UNKNOWN_COUNT = '-';
  // BLU-787 disclosures — each condition is "does the resolver's fixed
  // behaviour diverge from what the screen currently shows", so a disclosure
  // only renders when it would actually surprise the operator.
  const showSortDisclosure = screenSortOrder === 'newest';
  const showQueueStatusDisclosure = screenQueueStatus === 'all';
  const showSearchDisclosure = hasActiveSearch;
  const showClientFilterDisclosure = isClientFiltered;
  const hasDisclosures =
    showSortDisclosure || showQueueStatusDisclosure || showSearchDisclosure || showClientFilterDisclosure;
  // Overlap is the interesting number: tick Express + Custom Prints and the
  // union is 459, not 490, because 31 orders carry both tags. Saying so is what
  // stops the count reading like a bug. Both operands are scope-only, so a row
  // that is BOTH hand-ticked and scope-matched cannot inflate this.
  const overlap = Math.max(0, tickedScopeSum - scopeUnionTotal);
  // ⚠ NEVER PREVIEW OFF A PLACEHOLDER. `placeholderData` keeps the previous
  // resolve on screen while a new key resolves — that is what stops the menu
  // blanking on every tick — but it also means `unionTaken` and the page's
  // `orderIds` still describe the PREVIOUS tick until the new result lands.
  // Without `!isLoading` an operator can tick a second scope and hit Preview
  // inside that window, and Confirm then creates the batch from the stale union.
  // Before `placeholderData` this was masked by accident: `unionTaken` fell to 0
  // during the gap, which disabled the button. Make the guard explicit instead
  // of depending on data disappearing.
  // ⚠ `!isError` is as load-bearing as `!isLoading`. TanStack keeps the previous
  // `data` when a REFETCH fails, and `isFetching` drops back to false, so
  // without it a failed refresh silently re-arms both actions against the stale
  // union — and the operator has no way to know the numbers are old. That
  // matters more for Create now than for Preview, since nothing downstream shows
  // them what they are about to commit.
  const canPreview = unionTaken > 0 && !disabled && !isLoading && !isError;

  return (
    <DropdownMenu>
      <DropdownMenuTrigger asChild>
        <Button variant="default" className="gap-2" disabled={disabled}>
          <Layers className="h-4 w-4" aria-hidden="true" />
          {t('batch.scopes.trigger')}
          {/* BLU-787: `isCreating` as well as `isLoading`. The direct-create path
              closes the menu on select, so without this the operator presses
              "Create batch now" and gets a greyed button and nothing else until
              the toast — multi-second silence at Ningbo over the HK proxy, on the
              one control built to be pressed fast. */}
          {isLoading || isCreating ? (
            <Loader2 className="h-3.5 w-3.5 animate-spin opacity-70" aria-hidden="true" />
          ) : (
            unionTaken > 0 && <span className="tabular-nums">({unionTaken})</span>
          )}
          <ChevronDown className="h-3 w-3 opacity-60" aria-hidden="true" />
        </Button>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end" className="w-[22rem]">
        <DropdownMenuLabel>{t('batch.scopes.menuTitle')}</DropdownMenuLabel>
        <DropdownMenuSeparator />

        {scopeListState === 'pending' && (
          <div
            className="flex items-center gap-2 px-2 py-3 text-sm text-muted-foreground"
            role="status"
          >
            <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden="true" />
            {t('batch.scopes.loading')}
          </div>
        )}

        {scopeListState === 'error' && (
          /* A failed resolve must never wear the "not set up yet" copy — that
             sends an operator who HAS buttons off to re-create them. Destructive
             tone, not the amber this panel already uses for advisory notes
             (overlap / trimmed), so a failure is not mistaken for an FYI. */
          <div className="px-2 py-3 space-y-2" role="alert">
            <p className="flex items-start gap-2 text-sm text-destructive text-pretty">
              <AlertCircle className="mt-0.5 h-3.5 w-3.5 shrink-0" aria-hidden="true" />
              {t('batch.scopes.loadError')}
            </p>
            <DropdownMenuItem
              onSelect={(event) => {
                event.preventDefault();
                onRetry();
              }}
              className="justify-center font-medium"
            >
              {t('batch.scopes.retry')}
            </DropdownMenuItem>
          </div>
        )}

        {scopeListState === 'unconfigured' && (
          /* Every org except Threadheads ships empty filter_tags, so this is the
             common first-run state — it must read as "not set up yet", never as
             an error. */
          <div className="px-2 py-3 space-y-2">
            <p className="text-sm text-muted-foreground text-pretty">
              {t('batch.scopes.emptyTitle')}
            </p>
            {/* asChild through an Item, not a bare <Button><Link>: the same
                RovingFocusGroup rule as the scope rows below. A raw link here
                would leave the ONLY action in the empty state unreachable by
                keyboard — the state a brand-new org lands in first. */}
            <DropdownMenuItem asChild className="justify-center font-medium">
              <Link to="/production/settings?area=orders">
                <Settings2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
                {t('batch.scopes.emptyAction')}
              </Link>
            </DropdownMenuItem>
          </div>
        )}

        {scopeListState === 'ready' && (
          <div className="max-h-[18rem] overflow-y-auto py-1">
            {scopes.map((scope) => {
              const checked = checkedScopeIds.has(scope.id);
              const isEmpty = scope.count === 0;
              return (
                /* DropdownMenuCheckboxItem, not a hand-rolled <label><Checkbox/>:
                   Radix's menu preventDefaults Tab inside its content and drives
                   arrow-key movement from RovingFocusGroup, which only registers
                   Item descendants. Plain labels are therefore unreachable by
                   keyboard and invisible to screen readers, which announce a menu
                   of one item. The count stays inside the item so it lands in the
                   accessible name — it is the number the control exists to convey. */
                <DropdownMenuCheckboxItem
                  key={scope.id}
                  checked={checked}
                  onCheckedChange={() => onToggleScope(scope.id)}
                  // Ticking must not close the menu — the whole point is building
                  // a union from several scopes in one visit.
                  onSelect={(event) => event.preventDefault()}
                  // A 0-match scope stays tickable rather than disabled: the count
                  // is live, and a disabled control reads as broken configuration
                  // instead of "nothing matches right now".
                  className={cn('justify-between gap-3 py-2', isEmpty && 'opacity-60')}
                >
                  <span className="truncate font-medium">{scope.id === EXPRESS_BATCH_ACTION_ID ? settingsT('express.scopeLabel')
                    : scope.isTagScope && scope.name.trim().toLowerCase() === 'express' ? settingsT('express.tagScopeLabel') : scope.name}</span>
                  <span className="shrink-0 tabular-nums text-muted-foreground">
                    {scope.count}
                  </span>
                </DropdownMenuCheckboxItem>
              );
            })}
          </div>
        )}

        {scopeListState === 'ready' && <DropdownMenuItem asChild>
          <Link to="/production/settings?area=orders#express">
            <Settings2 className="mr-2 h-3.5 w-3.5" aria-hidden="true" />
            {settingsT('express.editDefinition')}
          </Link>
        </DropdownMenuItem>}

        {/* BLU-787 — the two injections, tickable here so a batch can be built
            and created without leaving the menu. Brett, 2026-08-13.

            ⚠ OUTSIDE the scrolling scope list above. That list is
            `max-h-[18rem] overflow-y-auto`, so an org with 8+ scopes would push
            these out of sight, and they are ticked by DEFAULT — a default-on
            control that can only be found by scrolling is how redos silently
            stop being made.

            ⚠ Its own heading, and UNITS on the counts. Scopes are counted in
            ORDERS and redos in GARMENTS; printed bare in one column, three
            numbers in the same typography read as one currency and get added
            together. They also obey different rules: scopes trim at the
            batch-wide order cap, redos have their own separate cap, and neither
            injection follows the client filter (see the disclosures below). */}
        <DropdownMenuSeparator />
        <DropdownMenuLabel className="text-xs font-medium text-muted-foreground">
          {t('batch.scopes.alsoAddLabel')}
        </DropdownMenuLabel>
        <DropdownMenuCheckboxItem
          checked={includeRedos}
          onCheckedChange={onIncludeRedosChange}
          onSelect={(event) => event.preventDefault()}
          className="justify-between gap-3 py-2"
        >
          <span className="truncate font-medium">{t('batch.scopes.redosMenuLabel')}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {countsKnown ? t('batch.scopes.garmentCount', { count: redoCount }) : UNKNOWN_COUNT}
          </span>
        </DropdownMenuCheckboxItem>
        {includeRedos && redoTotalPending > redoCount && (
          <p className="px-8 pb-1 text-xs text-amber-700 dark:text-amber-400 text-pretty">
            {t('batch.includeExtras.redosTrimmedNote', { count: redoTotalPending - redoCount })}
          </p>
        )}
        <DropdownMenuCheckboxItem
          checked={includeRequeued}
          onCheckedChange={onIncludeRequeuedChange}
          onSelect={(event) => event.preventDefault()}
          className="justify-between gap-3 py-2"
        >
          <span className="truncate font-medium">{t('batch.scopes.requeuedMenuLabel')}</span>
          <span className="shrink-0 tabular-nums text-muted-foreground">
            {countsKnown && requeuedCount !== undefined
              ? t('batch.scopes.orderCount', { count: requeuedCount })
              : UNKNOWN_COUNT}
          </span>
        </DropdownMenuCheckboxItem>

        {(tickedCount > 0 || handPickedCount > 0 || droppedHandPicked > 0) && (
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-2 space-y-1.5">
              <div className="flex items-center justify-between text-sm">
                <span className="font-medium">{t('batch.scopes.unionLabel')}</span>
                <span className="tabular-nums font-semibold">{unionTotal}</span>
              </div>

              {/* ⚠ The union is SCOPE-resolved orders only. `createBatch` Step 3d
                  APPENDS re-queued orders on top (deduped against the union, so
                  the real addition is "up to" this many) and Step 3c attaches
                  redo garments. Without these two lines the menu states an order
                  count six lines under another order count and the batch lands
                  bigger than either — and on the direct-create path there is no
                  preview screen left to correct it. */}
              {includeRequeued && countsKnown && (requeuedCount ?? 0) > 0 && (
                <p className="text-xs text-muted-foreground text-pretty">
                  {t('batch.scopes.plusRequeued', { count: requeuedCount })}
                </p>
              )}
              {includeRedos && countsKnown && redoCount > 0 && (
                <p className="text-xs text-muted-foreground text-pretty">
                  {t('batch.scopes.plusRedos', { count: redoCount })}
                </p>
              )}

              {overlap > 0 && (
                <p className="text-xs text-muted-foreground text-pretty">
                  {t('batch.scopes.overlapNote', { count: overlap })}
                </p>
              )}

              {handPickedCount > 0 && (
                <p className="text-xs text-muted-foreground text-pretty">
                  {t('batch.scopes.handPickedNote', { count: handPickedCount })}
                </p>
              )}

              {droppedHandPicked > 0 && (
                /* Say it out loud. These rows were ticked on another tab (All /
                   Out of stock) and cannot be batched — createBatch would reject
                   the whole batch, so silently omitting them is not an option. */
                <p className="text-xs text-amber-700 dark:text-amber-400 text-pretty">
                  {t('batch.scopes.droppedHandPickedNote', { count: droppedHandPicked })}
                </p>
              )}

              {trimmed && (
                /* The cap TRIMS, it never throws — `custom_product` sits at 456
                   unbatched today, so this line renders on day one. */
                <p className="text-xs text-amber-700 dark:text-amber-400 text-pretty">
                  {t('batch.scopes.trimmedNote', {
                    taken: unionTaken,
                    total: unionTotal,
                    remaining: unionTotal - unionTaken,
                    cap,
                  })}
                </p>
              )}
            </div>
          </>
        )}

        {hasDisclosures && (
          /* BLU-787 disclosures — the resolver's reach vs the screen. Shown
             independent of tick state: an operator reading the menu while
             actively searching, or filtered to one client, should see this
             before ticking anything, not only after. */
          <>
            <DropdownMenuSeparator />
            <div className="px-2 py-2 space-y-1.5" role="note">
              {showSortDisclosure && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground text-pretty">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  {t('batch.scopes.sortDisclosure')}
                </p>
              )}
              {showQueueStatusDisclosure && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground text-pretty">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  {t('batch.scopes.queueStatusDisclosure')}
                </p>
              )}
              {showSearchDisclosure && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground text-pretty">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  {t('batch.scopes.searchDisclosure')}
                </p>
              )}
              {showClientFilterDisclosure && (
                <p className="flex items-start gap-1.5 text-xs text-muted-foreground text-pretty">
                  <Info className="mt-0.5 h-3 w-3 shrink-0" aria-hidden="true" />
                  {t('batch.scopes.clientFilterDisclosure')}
                </p>
              )}
            </div>
          </>
        )}

        <DropdownMenuSeparator />
        {tickedCount > 0 && (
          /* Was a raw <button> in the header label — unreachable by keyboard for
             the same RovingFocusGroup reason as the scope rows. As an Item it
             sits with the other action and is actually operable. */
          <DropdownMenuItem
            onSelect={(event) => {
              event.preventDefault();
              onClearScopes();
            }}
            className="justify-center text-xs font-normal text-muted-foreground"
          >
            {t('batch.scopes.clear')}
          </DropdownMenuItem>
        )}
        <DropdownMenuItem
          disabled={!canPreview}
          onSelect={(event) => {
            // Keep the menu open on a disabled press rather than closing with
            // nothing having happened.
            if (!canPreview) {
              event.preventDefault();
              return;
            }
            onPreview();
          }}
          className="justify-center font-medium"
        >
          {unionTaken > 0
            ? t('batch.scopes.previewWithCount', { count: unionTaken })
            : t('batch.scopes.previewEmpty')}
        </DropdownMenuItem>
        {/* Rendered unconditionally and disabled via `canPreview`, matching
            Preview above. Wrapping it in `unionTaken > 0` as well would make the
            item appear and disappear as counts resolve, moving the row the
            operator is aiming at. */}
        {(
          /* BLU-787 — create without previewing first. Brett, 2026-08-13:
             "there should be a button below preview that just creates the batch
             so I don't have to preview first."

             Preview stays FIRST and stays the visually heavier action, because
             it is the reversible one. This sits under it as the fast path for an
             operator who already knows what they ticked.

             Shares `canCreate` (= `canPreview`) deliberately. The `!isLoading`
             half is load-bearing, not cosmetic: `placeholderData` keeps the
             PREVIOUS union on screen while a new key resolves, so without it a
             second tick could create off the stale set. That guard matters more
             here than on Preview, since nothing downstream shows the operator
             what they are about to commit. */
          <DropdownMenuItem
            disabled={!canPreview}
            onSelect={() => onCreateNow()}
            className="justify-center font-medium"
          >
            {t('batch.scopes.createNowWithCount', { count: unionTaken })}
          </DropdownMenuItem>
        )}
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
